import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useHistory } from 'react-router-dom'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import AuthenticationService from '../general/AuthenticationService.js'

/* ───────────────────────── design tokens ────────────────────── */

const tokens = {
    bg: '#f1f5f9',
    card: '#ffffff',
    border: '#e8edf5',
    blue: '#3b82f6',
    blueHover: '#2563eb',
    bluePale: '#eff6ff',
    navy: '#0f172a',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    textMuted: '#94a3b8',
    success: '#10b981',
    successBg: '#ecfdf5',
    successBorder: '#a7f3d0',
    danger: '#ef4444',
    dangerBg: '#fef2f2',
    radius: 16,
    radiusSm: 10,
    shadow: '0 1px 3px rgba(15,23,42,.06), 0 1px 2px rgba(15,23,42,.04)',
    shadowMd: '0 4px 12px rgba(15,23,42,.08)',
    font: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
}

/* ───────────────────────── Section helper ───────────────────── */

function Section({ title, subtitle, icon, badge, action, children, style }) {
    return (
        <div
            style={{
                background: tokens.card,
                border: `1px solid ${tokens.border}`,
                borderRadius: tokens.radius,
                boxShadow: tokens.shadow,
                overflow: 'hidden',
                ...style,
            }}
        >
            {(title || subtitle) && (
                <div
                    style={{
                        padding: '18px 22px',
                        borderBottom: `1px solid ${tokens.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {icon && (
                            <span
                                style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 9,
                                    background: `${tokens.blue}12`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 16,
                                    flexShrink: 0,
                                }}
                            >
                                {icon}
                            </span>
                        )}
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {title && (
                                    <h2
                                        style={{
                                            margin: 0,
                                            fontSize: 15,
                                            fontWeight: 650,
                                            color: tokens.textPrimary,
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        {title}
                                    </h2>
                                )}
                                {badge != null && (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minWidth: 20,
                                            height: 20,
                                            borderRadius: 10,
                                            background: tokens.bluePale,
                                            color: tokens.blue,
                                            fontSize: 11,
                                            fontWeight: 650,
                                            padding: '0 6px',
                                        }}
                                    >
                                        {badge}
                                    </span>
                                )}
                            </div>
                            {subtitle && (
                                <p
                                    style={{
                                        margin: '2px 0 0',
                                        fontSize: 12.5,
                                        color: tokens.textMuted,
                                    }}
                                >
                                    {subtitle}
                                </p>
                            )}
                        </div>
                    </div>
                    {action && action}
                </div>
            )}
            {children}
        </div>
    )
}

/* ───────────────────────── ResultTable ───────────────────────── */

function ResultTable({
    title,
    icon,
    columns,
    data,
    renderRow,
    onEdit,
    onDelete,
    showActions = true,
}) {
    if (!data || data.length === 0) return null

    const hasActions = showActions && (onEdit || onDelete)

    return (
        <Section title={title} icon={icon} badge={data.length} style={{ marginBottom: 14 }}>
            <div style={{ overflowX: 'auto' }}>
                <table
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 13.5,
                        fontFamily: tokens.font,
                    }}
                >
                    <thead>
                        <tr>
                            <th style={thStyle}>N°</th>
                            {columns.map((col, i) => (
                                <th key={i} style={{ ...thStyle, textAlign: col.align || 'left' }}>
                                    {col.label}
                                </th>
                            ))}
                            {hasActions && onEdit && <th style={{ ...thStyle, width: 42 }}>
                                <span style={{ fontSize: 13, color: tokens.textMuted }}>✎</span>
                            </th>}
                            {hasActions && onDelete && <th style={{ ...thStyle, width: 42 }}>
                                <span style={{ fontSize: 13, color: tokens.textMuted }}>✕</span>
                            </th>}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((element, idx) => (
                            <tr
                                key={element.id || idx}
                                style={{ borderBottom: `1px solid ${tokens.border}` }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                                <td style={{ ...tdStyle, color: tokens.textMuted, fontWeight: 500, width: 48 }}>
                                    {idx + 1}
                                </td>
                                {renderRow(element, idx)}
                                {hasActions && onEdit && (
                                    <td style={{ ...tdStyle, width: 42 }}>
                                        <IconButton
                                            icon="✎"
                                            color={tokens.blue}
                                            bg={tokens.bluePale}
                                            onClick={() => onEdit(element.id)}
                                        />
                                    </td>
                                )}
                                {hasActions && onDelete && (
                                    <td style={{ ...tdStyle, width: 42 }}>
                                        <IconButton
                                            icon="✕"
                                            color={tokens.danger}
                                            bg={tokens.dangerBg}
                                            onClick={() => onDelete(element.id)}
                                        />
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Section>
    )
}

const thStyle = {
    textAlign: 'left',
    padding: '10px 14px',
    fontSize: 11,
    fontWeight: 600,
    color: tokens.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: `1px solid ${tokens.border}`,
    background: '#fafbfd',
    whiteSpace: 'nowrap',
}

const tdStyle = {
    padding: '11px 14px',
    color: tokens.textPrimary,
    fontSize: 13.5,
}

/* ───────────────────────── IconButton ───────────────────────── */

function IconButton({ icon, color, bg, onClick }) {
    const [hovered, setHovered] = useState(false)
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                border: 'none',
                background: hovered ? bg : 'transparent',
                color: hovered ? color : tokens.textMuted,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                transition: 'all .15s',
            }}
        >
            {icon}
        </button>
    )
}

/* ───────────────────────── Chip / Tag ───────────────────────── */

function Tag({ children }) {
    return (
        <span
            style={{
                display: 'inline-block',
                background: '#f1f5f9',
                borderRadius: 6,
                padding: '2px 8px',
                fontSize: 12,
                color: tokens.textSecondary,
                fontWeight: 500,
                marginRight: 4,
                marginBottom: 3,
                lineHeight: '20px',
            }}
        >
            {children}
        </span>
    )
}

/* ───────────────────────── CellLink ─────────────────────────── */

function CellLink({ to, children }) {
    return (
        <Link
            to={to}
            style={{
                color: tokens.blue,
                textDecoration: 'none',
                fontWeight: 550,
            }}
        >
            {children}
        </Link>
    )
}

/* ───────────────────────── LoadingBar ────────────────────────── */

function LoadingBar({ visible }) {
    if (!visible) return null
    return (
        <div style={{ position: 'relative', height: 3, overflow: 'hidden', borderRadius: 2, marginBottom: 20 }}>
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    background: tokens.border,
                }}
            />
            <div
                className="zenyth-loading-bar"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '40%',
                    background: `linear-gradient(90deg, ${tokens.blue}, ${tokens.blueHover})`,
                    borderRadius: 2,
                }}
            />
            <style>{`
                @keyframes zenyth-slide {
                    0%   { transform: translateX(-100%); }
                    100% { transform: translateX(350%); }
                }
                .zenyth-loading-bar {
                    animation: zenyth-slide 1.2s ease-in-out infinite;
                }
            `}</style>
        </div>
    )
}

/* ───────────────────────── debounce hook ─────────────────────── */

function useDebouncedValue(value, delay = 350) {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(timer)
    }, [value, delay])
    return debounced
}

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════ */

const SEARCH_CATEGORIES = ['sociedades', 'clientes', 'personasFisicas', 'domicilios', 'documentos']

function BusquedaGeneralComponent(props) {
    const history = useHistory()

    const [query, setQuery] = useState('')
    const [loading, setLoading] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)
    const [results, setResults] = useState({
        sociedades: [],
        clientes: [],
        personasFisicas: [],
        domicilios: [],
        documentos: [],
    })
    const [message] = useState(props.message || '')

    /* ref para cancelar búsquedas obsoletas */
    const searchIdRef = useRef(0)
    const inputRef = useRef(null)

    const debouncedQuery = useDebouncedValue(query, 350)

    /* ── auth guard ── */
    useEffect(() => {
        if (!AuthenticationService.haveAuthorization('READ_BUSQUEDA')) {
            alert('Usuario no autorizado')
            history.push('/login')
            return
        }
        /* auto-focus */
        inputRef.current?.focus()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    /* ── search effect (debounced) ── */
    useEffect(() => {
        if (debouncedQuery.length < 3) {
            setResults({
                sociedades: [],
                clientes: [],
                personasFisicas: [],
                domicilios: [],
                documentos: [],
            })
            setHasSearched(false)
            return
        }

        const currentSearchId = ++searchIdRef.current
        setLoading(true)
        setHasSearched(true)

        const term = debouncedQuery.toUpperCase()

        Promise.all(
            SEARCH_CATEGORIES.map((cat) =>
                ElementsDataService.retrieveSearch(term, cat)
                    .then((r) => ({ cat, data: r.data }))
                    .catch(() => ({ cat, data: [] }))
            )
        ).then((responses) => {
            /* descarta si ya hubo otra búsqueda más nueva */
            if (currentSearchId !== searchIdRef.current) return

            const newResults = {}
            responses.forEach(({ cat, data }) => {
                newResults[cat] = data || []
            })
            setResults(newResults)
            setLoading(false)
        })
    }, [debouncedQuery])

    /* ── computed ── */
    const totalResults = Object.values(results).reduce(
        (sum, arr) => sum + (arr ? arr.length : 0),
        0
    )
    const noResults = hasSearched && !loading && totalResults === 0

    /* ────────────────── RENDER ────────────────── */
    return (
        <div
            style={{
                minHeight: '100vh',
                background: tokens.bg,
                fontFamily: tokens.font,
                padding: '32px 32px 64px',
            }}
        >
            <div style={{ maxWidth: 1120, margin: '0 auto' }}>

                {/* Page header */}
                <div style={{ marginBottom: 28 }}>
                    <h1
                        style={{
                            margin: 0,
                            fontSize: 26,
                            fontWeight: 700,
                            color: tokens.navy,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Búsqueda General
                    </h1>
                    <p
                        style={{
                            margin: '4px 0 0',
                            fontSize: 14,
                            color: tokens.textSecondary,
                        }}
                    >
                        Busca en sociedades, proyectos, personas físicas, domicilios y documentos
                    </p>
                </div>

                {/* Success banner */}
                {message && (
                    <div
                        style={{
                            background: tokens.successBg,
                            border: `1px solid ${tokens.successBorder}`,
                            borderRadius: tokens.radiusSm,
                            padding: '12px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 20,
                            fontSize: 13.5,
                            color: '#065f46',
                            fontWeight: 500,
                        }}
                    >
                        <span>✓</span>
                        {message}
                    </div>
                )}

                {/* ── Search input ── */}
                <div
                    style={{
                        background: tokens.card,
                        border: `1px solid ${query.length > 0 ? tokens.blue : tokens.border}`,
                        borderRadius: tokens.radius,
                        boxShadow: query.length > 0 ? `0 0 0 3px ${tokens.blue}18` : tokens.shadow,
                        padding: '4px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        transition: 'all .2s ease',
                        marginBottom: 8,
                    }}
                >
                    <span
                        style={{
                            fontSize: 20,
                            color: query.length > 0 ? tokens.blue : tokens.textMuted,
                            padding: '8px 4px 8px 10px',
                            transition: 'color .2s',
                            flexShrink: 0,
                        }}
                    >
                        ⌕
                    </span>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Escribe al menos 3 caracteres para buscar..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value.toUpperCase())}
                        style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            fontSize: 15,
                            fontFamily: tokens.font,
                            color: tokens.textPrimary,
                            padding: '12px 0',
                            letterSpacing: '0.01em',
                        }}
                    />
                    {query.length > 0 && (
                        <button
                            onClick={() => setQuery('')}
                            style={{
                                width: 28,
                                height: 28,
                                borderRadius: 7,
                                border: 'none',
                                background: '#f1f5f9',
                                color: tokens.textMuted,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 14,
                                fontWeight: 600,
                                marginRight: 6,
                                flexShrink: 0,
                            }}
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* ── Result count / status ── */}
                {hasSearched && !loading && totalResults > 0 && (
                    <div
                        style={{
                            fontSize: 13,
                            color: tokens.textMuted,
                            fontWeight: 500,
                            padding: '6px 0 14px',
                        }}
                    >
                        {totalResults} resultado{totalResults !== 1 ? 's' : ''} para "{debouncedQuery}"
                    </div>
                )}

                {!hasSearched && (
                    <div style={{ padding: '6px 0 14px', fontSize: 13, color: tokens.textMuted }}>
                        {query.length > 0 && query.length < 3
                            ? `Escribe ${3 - query.length} carácter${3 - query.length > 1 ? 'es' : ''} más...`
                            : ''}
                    </div>
                )}

                {/* ── Loading ── */}
                <LoadingBar visible={loading} />

                {/* ── No results ── */}
                {noResults && (
                    <div
                        style={{
                            background: tokens.card,
                            border: `1px solid ${tokens.border}`,
                            borderRadius: tokens.radius,
                            padding: '52px 24px',
                            textAlign: 'center',
                            boxShadow: tokens.shadow,
                        }}
                    >
                        <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.6 }}>🔍</div>
                        <p
                            style={{
                                margin: 0,
                                fontSize: 15,
                                fontWeight: 600,
                                color: tokens.textPrimary,
                            }}
                        >
                            Sin resultados
                        </p>
                        <p
                            style={{
                                margin: '4px 0 0',
                                fontSize: 13,
                                color: tokens.textSecondary,
                            }}
                        >
                            No se encontraron coincidencias para "{debouncedQuery}"
                        </p>
                    </div>
                )}

                {/* ── Empty state (before search) ── */}
                {!hasSearched && query.length === 0 && (
                    <div
                        style={{
                            background: tokens.card,
                            border: `1px solid ${tokens.border}`,
                            borderRadius: tokens.radius,
                            padding: '56px 24px',
                            textAlign: 'center',
                            boxShadow: tokens.shadow,
                        }}
                    >
                        <div style={{ fontSize: 44, marginBottom: 14, opacity: 0.5 }}>⌕</div>
                        <p
                            style={{
                                margin: 0,
                                fontSize: 15,
                                fontWeight: 600,
                                color: tokens.textPrimary,
                            }}
                        >
                            Busca en todo el sistema
                        </p>
                        <p
                            style={{
                                margin: '6px 0 0',
                                fontSize: 13,
                                color: tokens.textSecondary,
                                maxWidth: 360,
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                lineHeight: 1.5,
                            }}
                        >
                            Sociedades, proyectos, personas físicas, domicilios y documentos — todo desde un solo lugar
                        </p>
                    </div>
                )}

                {/* ═══════ RESULT TABLES ═══════ */}

                {/* Sociedades */}
                <ResultTable
                    title="Sociedades"
                    icon="🏢"
                    data={results.sociedades}
                    onEdit={props.updateElementClicked}
                    onDelete={props.deleteElementClicked}
                    columns={[
                        { label: 'Proyecto' },
                        { label: 'Razón Social' },
                        { label: 'RFC' },
                        { label: 'Resp. Contabilidad' },
                        { label: 'Auxiliar Contable' },
                    ]}
                    renderRow={(el) => (
                        <>
                            <td style={tdStyle}>
                                {el.clientes &&
                                    el.clientes.map((c, i) => (
                                        <Tag key={c.id || i}>{c.nombreCliente}</Tag>
                                    ))}
                            </td>
                            <td style={tdStyle}>
                                <CellLink to={`/sociedades/reportes/${el.id}`}>
                                    {el.razonSocial}
                                </CellLink>
                            </td>
                            <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums' }}>
                                <CellLink to={`/sociedades/reportesContables/${el.rfc}`}>
                                    {el.rfc}
                                </CellLink>
                            </td>
                            <td style={tdStyle}>{el.responsableContable}</td>
                            <td style={tdStyle}>{el.auxiliarContable}</td>
                        </>
                    )}
                />

                {/* Proyectos (Clientes) */}
                <ResultTable
                    title="Proyectos"
                    icon="📁"
                    data={results.clientes}
                    onEdit={props.updateElementClicked}
                    onDelete={props.deleteElementClicked}
                    columns={[
                        { label: 'Nombre Proyecto' },
                        { label: 'Propietario' },
                    ]}
                    renderRow={(el) => (
                        <>
                            <td style={tdStyle}>
                                <CellLink to={`/clientes/reportes/${el.id}`}>
                                    {el.nombreCliente}
                                </CellLink>
                            </td>
                            <td style={tdStyle}>{el.propietario}</td>
                        </>
                    )}
                />

                {/* Personas Físicas */}
                <ResultTable
                    title="Personas Físicas"
                    icon="👤"
                    data={results.personasFisicas}
                    onEdit={props.updateElementClicked}
                    onDelete={props.deleteElementClicked}
                    columns={[
                        { label: 'Apellido Paterno' },
                        { label: 'Apellido Materno' },
                        { label: 'Nombre(s)' },
                        { label: 'RFC' },
                    ]}
                    renderRow={(el) => (
                        <>
                            <td style={tdStyle}>{el.apellidoPaterno}</td>
                            <td style={tdStyle}>{el.apellidoMaterno}</td>
                            <td style={tdStyle}>{el.nombre}</td>
                            <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums' }}>
                                <CellLink to={`/personas-fisicas/reportes/${el.id}`}>
                                    {el.rfc}
                                </CellLink>
                            </td>
                        </>
                    )}
                />

                {/* Domicilios */}
                <ResultTable
                    title="Domicilios"
                    icon="📍"
                    data={results.domicilios}
                    onEdit={props.updateElementClicked}
                    onDelete={props.deleteElementClicked}
                    columns={[
                        { label: 'Calle' },
                        { label: 'N. Ext', align: 'right' },
                        { label: 'N. Int', align: 'right' },
                        { label: 'Colonia' },
                        { label: 'Municipio' },
                        { label: 'Estado' },
                        { label: 'Teléfono' },
                        { label: 'Estatus' },
                        { label: 'Tipo' },
                        { label: 'Sociedades' },
                    ]}
                    renderRow={(el) => (
                        <>
                            <td style={tdStyle}>
                                <CellLink to={`/domicilios/reportes/${el.id}`}>
                                    {el.calle}
                                </CellLink>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                {el.numeroExt}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                {el.numeroInterior}
                            </td>
                            <td style={tdStyle}>{el.colonia}</td>
                            <td style={tdStyle}>{el.municipio}</td>
                            <td style={tdStyle}>{el.estado}</td>
                            <td style={tdStyle}>{el.telefono}</td>
                            <td style={tdStyle}>{el.estatus}</td>
                            <td style={tdStyle}>{el.tipo}</td>
                            <td style={tdStyle}>
                                {el.sociedadesNames &&
                                    el.sociedadesNames.map((s, i) => <Tag key={i}>{s}</Tag>)}
                            </td>
                        </>
                    )}
                />

                {/* Documentos */}
                <ResultTable
                    title="Documentos"
                    icon="📄"
                    data={results.documentos}
                    onEdit={props.updateElementClicked}
                    onDelete={props.deleteElementClicked}
                    columns={[
                        { label: 'Sociedades' },
                        { label: 'Tipo Documento' },
                        { label: 'Asuntos' },
                        { label: 'Fecha' },
                    ]}
                    renderRow={(el) => (
                        <>
                            <td style={tdStyle}>
                                {el.sociedades &&
                                    el.sociedades.map((s, i) => (
                                        <Tag key={s.id || i}>{s.razonSocial}</Tag>
                                    ))}
                            </td>
                            <td style={tdStyle}>
                                <CellLink to={`/documentos/reportes/${el.id}`}>
                                    {el.tipoDocumento}
                                </CellLink>
                            </td>
                            <td style={tdStyle}>
                                <CellLink to={`/documentos/reportes/${el.id}`}>
                                    {el.asuntos || el.tipoDocumento}
                                </CellLink>
                            </td>
                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{el.fecha}</td>
                        </>
                    )}
                />
            </div>
        </div>
    )
}

export default BusquedaGeneralComponent
